/**
 * ReplayModal - Interactive Game Replay Player & Library Modal
 * Allows players to browse saved matches, step through historical moves,
 * adjust playback speed, scrub timelines, inspect hands, and export/share games.
 */

import { GameReplayManager } from '../game/GameReplayManager.js';
import { CribbageScorer } from '../scoring/CribbageScorer.js';
import { GAME_BRANDS } from '../profile/Profile.js';
import { TraditionalCard } from './TraditionalCard.js';

export class ReplayModal {
  static OVERLAY_ID = 'crazyReplayOverlay';
  static MODAL_ID = 'crazyReplayModal';

  static activeReplay = null;
  static currentStep = 0;
  static isPlaying = false;
  static playbackSpeed = 1; // 0.5 | 1 | 2 | 4
  static autoPauseOnShow = true; // Auto-pause after counting each hand and crib
  static playbackTimer = null;
  static keydownHandler = null;
  static _currentView = null; // 'player' | 'library'
  static _renderedReplayId = null;
  static _selectedBrand = 'all';

  /**
   * Opens the Replay Modal.
   * If a replay or replay ID is passed, launches the interactive viewer;
   * otherwise opens the saved replays library.
   * @param {Object|string} [replayOrId]
   * @param {string} [brand='all']
   */
  static open(replayOrId = null, brand = null) {
    if (typeof document === 'undefined') return;

    if (brand) {
      ReplayModal._selectedBrand = brand;
    }

    let overlay = document.getElementById(ReplayModal.OVERLAY_ID);
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = ReplayModal.OVERLAY_ID;
      overlay.className = 'replay-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', 'Crazy Cribbage Game Replays');
      document.body.appendChild(overlay);
    }

    ReplayModal.stopPlayback();

    overlay.classList.add('is-opening');
    setTimeout(() => {
      if (overlay) overlay.classList.remove('is-opening');
    }, 250);

    if (replayOrId) {
      const rep = typeof replayOrId === 'string' ? GameReplayManager.getReplayById(replayOrId) : replayOrId;
      if (rep) {
        ReplayModal.activeReplay = rep;
        ReplayModal.currentStep = 0;
        ReplayModal.renderPlayer();
      } else {
        ReplayModal.renderLibrary(ReplayModal._selectedBrand);
      }
    } else {
      ReplayModal.renderLibrary(ReplayModal._selectedBrand);
    }

    overlay.style.display = 'flex';
    overlay.onclick = (e) => {
      if (e.target === overlay) {
        ReplayModal.close();
      }
    };
    ReplayModal.attachKeyboardControls();
  }

  /**
   * Opens the library view explicitly, optionally focused on a brand.
   * @param {string} [brand='all']
   */
  static openLibrary(brand = 'all') {
    ReplayModal.stopPlayback();
    ReplayModal.activeReplay = null;
    ReplayModal._currentView = null;
    ReplayModal._renderedReplayId = null;
    ReplayModal._selectedBrand = brand || 'all';
    ReplayModal.open(null, brand || 'all');
  }

  /**
   * Closes the Replay Modal.
   */
  static close() {
    ReplayModal.stopPlayback();
    ReplayModal.detachKeyboardControls();
    ReplayModal._currentView = null;
    ReplayModal._renderedReplayId = null;
    if (typeof document !== 'undefined') {
      const overlay = document.getElementById(ReplayModal.OVERLAY_ID);
      if (overlay) overlay.style.display = 'none';
    }

    const isGameOver = typeof window !== 'undefined' && ((window.state && window.state.over) || window._lastGameOverHtml);
    if (isGameOver) {
      if (typeof window !== 'undefined' && typeof window.showGameWonModal === 'function') {
        window.showGameWonModal();
      } else if (typeof GameEngine !== 'undefined' && typeof GameEngine.showGameWonModal === 'function') {
        GameEngine.showGameWonModal();
      }
    }
  }

  /**
   * Attaches keyboard shortcut listener.
   */
  static attachKeyboardControls() {
    ReplayModal.detachKeyboardControls();
    ReplayModal.keydownHandler = (e) => {
      const overlay = document.getElementById(ReplayModal.OVERLAY_ID);
      if (!overlay || overlay.style.display === 'none') return;

      if (e.key === 'Escape') {
        e.preventDefault();
        ReplayModal.close();
      } else if (ReplayModal.activeReplay) {
        if (e.key === ' ' || e.code === 'Space') {
          e.preventDefault();
          ReplayModal.togglePlayPause();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          ReplayModal.stepForward();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          ReplayModal.stepBackward();
        } else if (e.key === 'Home') {
          e.preventDefault();
          ReplayModal.goToStep(0);
        } else if (e.key === 'End') {
          e.preventDefault();
          const max = (ReplayModal.activeReplay.events?.length || 1) - 1;
          ReplayModal.goToStep(max);
        }
      }
    };
    window.addEventListener('keydown', ReplayModal.keydownHandler);
  }

  /**
   * Detaches keyboard shortcut listener.
   */
  static detachKeyboardControls() {
    if (ReplayModal.keydownHandler && typeof window !== 'undefined') {
      window.removeEventListener('keydown', ReplayModal.keydownHandler);
      ReplayModal.keydownHandler = null;
    }
  }

  /**
   * Formats a card into a styled HTML element badge.
   * @param {Object} card
   * @param {boolean} [highlight=false]
   * @param {boolean} [played=false]
   * @returns {string}
   */
  static formatCardHtml(card, highlight = false, played = false) {
    if (!card) return '<div class="replay-card replay-card-empty">—</div>';
    const c = TraditionalCard.normalize(card);
    const color = c.isJoker ? '#9333ea' : (c.isRed ? '#ef4444' : '#1e293b');

    return `
      <div class="replay-card ${c.isRed ? 'replay-red' : 'replay-black'}${highlight ? ' is-highlighted' : ''}${played ? ' is-played' : ''}" style="color:${color}; position:relative; overflow:hidden;">
        ${TraditionalCard.renderHtml(card)}
      </div>
    `;
  }

  /**
   * Compares two cards for equality by id or rank+suit.
   * @param {Object} a
   * @param {Object} b
   * @returns {boolean}
   */
  static sameCard(a, b) {
    if (!a || !b) return false;
    if (a.id && b.id && a.id === b.id) return true;
    const ra = a.r !== undefined ? a.r : a.rank;
    const rb = b.r !== undefined ? b.r : b.rank;
    const sa = (a.s !== undefined ? a.s : a.suit || '').toUpperCase();
    const sb = (b.s !== undefined ? b.s : b.suit || '').toUpperCase();
    return ra === rb && sa === sb;
  }

  /**
   * Returns a copy of hand with played cards removed.
   * @param {Array} hand
   * @param {Array} played
   * @returns {Array}
   */
  static removeCards(hand, played) {
    if (!Array.isArray(hand)) return [];
    const remaining = [...hand];
    if (!Array.isArray(played)) return remaining;
    for (const p of played) {
      const idx = remaining.findIndex(c => ReplayModal.sameCard(c, p));
      if (idx !== -1) {
        remaining.splice(idx, 1);
      }
    }
    return remaining;
  }

  /**
   * Formats combo notes and points into stylish badge pills.
   * @param {Array} combos
   * @param {number} points
   * @returns {string}
   */
  static formatComboPills(combos, points = 0) {
    const pills = [];
    if (points !== undefined && points !== null) {
      pills.push(`<span class="replay-combo-pill is-total">+${points} pts</span>`);
    }

    if (Array.isArray(combos) && combos.length) {
      for (const c of combos) {
        let text = '';
        if (typeof c === 'string') {
          text = c;
        } else if (c && typeof c === 'object') {
          if (c.why && c.n !== undefined) {
            text = `${c.why} (${c.n})`;
          } else if (c.label && c.points !== undefined) {
            text = `${c.label} (${c.points})`;
          } else if (c.desc) {
            text = c.desc;
          } else if (c.description) {
            text = c.description;
          } else if (c.name) {
            text = c.name;
          } else {
            text = JSON.stringify(c);
          }
        }
        if (text) {
          const formatted = text.charAt(0).toUpperCase() + text.slice(1);
          pills.push(`<span class="replay-combo-pill">${formatted}</span>`);
        }
      }
    } else if (points === 0) {
      pills.push('<span class="replay-combo-pill is-zero">0 points (No combinations)</span>');
    }

    return pills.join('');
  }

  /**
   * Renders the interactive replay player view.
   * Builds the shell once if needed, then performs smooth in-place DOM updates without re-building or flashing.
   */
  static renderPlayer() {
    const overlay = document.getElementById(ReplayModal.OVERLAY_ID);
    if (!overlay || !ReplayModal.activeReplay) return;

    const rep = ReplayModal.activeReplay;
    const events = rep.events || [];
    const event = events[ReplayModal.currentStep] || events[0] || {};
    const totalSteps = Math.max(1, events.length);
    const targetScore = rep.target || 121;

    const p1 = rep.players?.p1 || { name: 'Player 1', avatar: '🤠' };
    const p2 = rep.players?.p2 || { name: 'Player 2', avatar: '🤖' };

    const modal = document.getElementById(ReplayModal.MODAL_ID);
    const isPlayerShellPresent = modal && ReplayModal._currentView === 'player' && ReplayModal._renderedReplayId === rep.id;

    if (!isPlayerShellPresent) {
      ReplayModal._currentView = 'player';
      ReplayModal._renderedReplayId = rep.id;

      overlay.innerHTML = `
        <div class="replay-modal-wrap" id="${ReplayModal.MODAL_ID}">
          <!-- Header -->
          <div class="replay-header">
            <div class="replay-header-left">
              <button type="button" class="btn-ghost replay-btn-sm" id="btnReplayBackToLib" title="Back to Replays List">
                ◀ Replay Library
              </button>
              <div class="replay-title-wrap">
                <h2 class="replay-title">🎬 Match Replay: ${p1.name} vs ${p2.name}</h2>
                <div class="replay-meta-pills">
                  <span class="replay-badge badge-brand">${rep.brandLabel || 'Crazy Cribbage'}</span>
                  <span class="replay-badge badge-target">${targetScore} Points</span>
                  <span class="replay-badge badge-date">${rep.dateLabel || 'Match'}</span>
                  ${rep.winnerName ? `<span class="replay-badge badge-winner">🏆 Winner: ${rep.winnerName} (${rep.finalScores?.p1}–${rep.finalScores?.p2})</span>` : ''}
                </div>
              </div>
            </div>
            <div class="replay-header-right">
              <button type="button" class="btn-ghost replay-btn-close" id="btnReplayClose" title="Close Replay">
                ✕
              </button>
            </div>
          </div>

          <!-- Scoreboard Progress Track -->
          <div class="replay-scoreboard">
            <div class="replay-score-card" id="repP1ScoreCard">
              <div class="replay-player-row">
                <span class="replay-avatar">${p1.avatar || '🤠'}</span>
                <span class="replay-player-name">${p1.name}</span>
                <span class="replay-player-score" id="repP1Score">0 <span class="score-denom">/ ${targetScore}</span></span>
              </div>
              <div class="replay-score-track">
                <div class="replay-score-fill fill-p1" id="repP1ScoreFill" style="width: 0%;"></div>
              </div>
            </div>

            <div class="replay-round-pill">
              <span class="round-label">ROUND</span>
              <span class="round-num" id="repRoundNum">1</span>
            </div>

            <div class="replay-score-card" id="repP2ScoreCard">
              <div class="replay-player-row">
                <span class="replay-avatar">${p2.avatar || '🤖'}</span>
                <span class="replay-player-name">${p2.name}</span>
                <span class="replay-player-score" id="repP2Score">0 <span class="score-denom">/ ${targetScore}</span></span>
              </div>
              <div class="replay-score-track">
                <div class="replay-score-fill fill-p2" id="repP2ScoreFill" style="width: 0%;"></div>
              </div>
            </div>
          </div>

          <!-- Action Narration Banner -->
          <div class="replay-narration-box" id="replayNarration">
            <div class="narration-icon" id="repNarrationIcon">💬</div>
            <div class="narration-text" id="repNarrationText">Play progress...</div>
            <div class="replay-pause-alert" id="repPauseNotice" style="display: none;"></div>
          </div>

          <!-- Replay Table Layout -->
          <div class="replay-table-grid">
            <!-- Left: Starter & Crib -->
            <div class="replay-col-side">
              <div class="replay-box" id="repStarterBox">
                <div class="replay-box-title">✂️ Starter Card</div>
                <div class="replay-cards-row" id="repStarterArea">
                  <div class="replay-card replay-card-back">🂠</div>
                </div>
              </div>
              <div class="replay-box" id="repCribBox">
                <div class="replay-box-title" id="repCribTitle">🎁 Dealer Crib</div>
                <div class="replay-cards-row" id="repCribArea">
                  <span class="replay-dim-text">Hidden in crib</span>
                </div>
                <div class="replay-combos-breakdown" id="repCribCombos" style="display: none;"></div>
              </div>
            </div>

            <!-- Center: Pegging Play Area & Count Dial -->
            <div class="replay-col-center">
              <div class="replay-box replay-box-play">
                <div class="replay-box-title replay-box-title-center">
                  <span>🃏 Pegging Sequence</span>
                  <span class="replay-count-badge" id="repCountBadge">Count: <strong>0</strong> / 31</span>
                </div>
                <div class="replay-pile-area" id="repPileArea">
                  <span class="replay-dim-text">No cards played yet this turn</span>
                </div>
              </div>

              <!-- Player Hands Area -->
              <div class="replay-hands-area">
                <div class="replay-hand-box" id="repP1HandBox">
                  <div class="replay-hand-main">
                    <div class="replay-hand-label" id="repP1HandLabel">${p1.name}'s Hand:</div>
                    <div class="replay-cards-row" id="repP1HandArea">
                      <span class="replay-dim-text">No cards</span>
                    </div>
                  </div>
                  <div class="replay-combos-breakdown" id="repP1Combos" style="display: none;"></div>
                </div>
                <div class="replay-hand-box" id="repP2HandBox">
                  <div class="replay-hand-main">
                    <div class="replay-hand-label" id="repP2HandLabel">${p2.name}'s Hand:</div>
                    <div class="replay-cards-row" id="repP2HandArea">
                      <span class="replay-dim-text">No cards</span>
                    </div>
                  </div>
                  <div class="replay-combos-breakdown" id="repP2Combos" style="display: none;"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Timeline & Playback Controller Bar -->
          <div class="replay-controller-bar">
            <!-- Scrubber Range -->
            <div class="replay-scrubber-row">
              <span class="replay-step-info" id="repStepInfo">Step <strong>1</strong> of ${totalSteps} (Round 1)</span>
              <input type="range" class="replay-scrubber-slider" id="replayScrubber" min="0" max="${totalSteps - 1}" value="${ReplayModal.currentStep}" aria-label="Replay Timeline Scrubber" />
            </div>

            <!-- Controls Buttons -->
            <div class="replay-buttons-row">
              <div class="replay-controls-group">
                <button type="button" class="btn-ghost replay-ctrl-btn" id="btnRepPrevRound" title="Previous Round (Home)">⏮ Round</button>
                <button type="button" class="btn-ghost replay-ctrl-btn" id="btnRepStepBack" title="Step Back (Left Arrow)">◀ Step</button>
                <button type="button" class="btn-gold replay-play-btn" id="btnRepPlayPause" title="Play / Pause (Spacebar)">
                  ${ReplayModal.isPlaying ? '⏸ Pause' : '▶ Play'}
                </button>
                <button type="button" class="btn-ghost replay-ctrl-btn" id="btnRepStepForward" title="Step Forward (Right Arrow)">Step ▶</button>
                <button type="button" class="btn-ghost replay-ctrl-btn" id="btnRepNextRound" title="Next Round (End)">Round ⏭</button>
              </div>

              <!-- Speed selector & Auto-pause toggle -->
              <div class="replay-options-group">
                <div class="replay-speed-group" role="group" aria-label="Playback Speed">
                  <span class="speed-label">Speed:</span>
                  ${[0.5, 1, 2, 4].map(spd => `
                    <button type="button" class="replay-speed-btn${ReplayModal.playbackSpeed === spd ? ' is-active' : ''}" data-speed="${spd}">
                      ${spd}x
                    </button>
                  `).join('')}
                </div>

                <label class="replay-check-label" title="Automatically pause replay after counting each hand and crib">
                  <input type="checkbox" id="chkAutoPauseShow" ${ReplayModal.autoPauseOnShow ? 'checked' : ''} />
                  <span>Pause on Hand/Crib</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      `;

      ReplayModal.bindPlayerEvents();
    }

    // Dynamic state updates (smooth in-place DOM updates without re-building modal or triggering flash)
    const isShowHand = event.type === 'show_hand';
    const isShowCrib = event.type === 'show_crib';
    const isP1Scoring = isShowHand && (event.who === 'p1' || event.who === 'you');
    const isP2Scoring = isShowHand && (event.who === 'p2' || event.who === 'cpu');
    const isCribScoring = isShowCrib;

    const curIdx = ReplayModal.currentStep;

    // Determine round boundaries: every hand starts at a 'round_start' event
    let roundStartIdx = 0;
    for (let i = curIdx; i >= 0; i--) {
      if (events[i].type === 'round_start') {
        roundStartIdx = i;
        break;
      }
    }
    let nextRoundStartIdx = events.length;
    for (let i = roundStartIdx + 1; i < events.length; i++) {
      if (events[i].type === 'round_start') {
        nextRoundStartIdx = i;
        break;
      }
    }
    const roundEndIdx = nextRoundStartIdx - 1;
    const roundNum = events[roundStartIdx]?.round || event.round || 1;

    // 1. Gather all round-specific data between roundStartIdx and roundEndIdx
    let p1ShowCards = null;
    let p2ShowCards = null;
    let p1Dealt = null;
    let p2Dealt = null;
    const p1Discards = [];
    const p2Discards = [];
    const p1PeggedAll = [];
    const p2PeggedAll = [];
    let hasCutOccurred = false;

    for (let i = roundStartIdx; i <= roundEndIdx; i++) {
      const e = events[i];
      if (e.type === 'show_hand' && Array.isArray(e.cards) && e.cards.length) {
        if ((e.who === 'p1' || e.who === 'you') && !p1ShowCards) p1ShowCards = [...e.cards];
        else if ((e.who === 'p2' || e.who === 'cpu') && !p2ShowCards) p2ShowCards = [...e.cards];
      } else if (e.type === 'deal' && e.hands) {
        if (!p1Dealt && (e.hands.p1 || e.hands.you)) p1Dealt = [...(e.hands.p1 || e.hands.you)];
        if (!p2Dealt && (e.hands.p2 || e.hands.cpu)) p2Dealt = [...(e.hands.p2 || e.hands.cpu)];
      } else if (e.type === 'discard') {
        if (e.who === 'p1' || e.who === 'you') p1Discards.push(...(e.discards || e.cards || []));
        else if (e.who === 'p2' || e.who === 'cpu') p2Discards.push(...(e.discards || e.cards || []));
      } else if (e.type === 'peg_play' && e.card) {
        if (e.who === 'p1' || e.who === 'you') p1PeggedAll.push(e.card);
        else if (e.who === 'p2' || e.who === 'cpu') p2PeggedAll.push(e.card);
      }
    }

    for (let i = roundStartIdx; i <= curIdx; i++) {
      if (events[i].type === 'starter_cut' || events[i].type === 'peg_play' || events[i].type === 'show_hand' || events[i].type === 'show_crib') {
        hasCutOccurred = true;
        break;
      }
    }

    // 2. Resolve the 4 kept cards for each player in this round
    let p1Kept = p1ShowCards;
    if (!p1Kept && p1PeggedAll.length >= 4) p1Kept = p1PeggedAll;
    if (!p1Kept && p1Dealt) {
      p1Kept = p1Discards.length ? ReplayModal.removeCards(p1Dealt, p1Discards) : (p1Dealt.length === 4 ? p1Dealt : (p1PeggedAll.length ? p1PeggedAll : p1Dealt));
    }
    if (!p1Kept && event.hands?.p1) p1Kept = [...event.hands.p1];
    if (!p1Kept) p1Kept = p1PeggedAll;

    let p2Kept = p2ShowCards;
    if (!p2Kept && p2PeggedAll.length >= 4) p2Kept = p2PeggedAll;
    if (!p2Kept && p2Dealt) {
      p2Kept = p2Discards.length ? ReplayModal.removeCards(p2Dealt, p2Discards) : (p2Dealt.length === 4 ? p2Dealt : (p2PeggedAll.length ? p2PeggedAll : p2Dealt));
    }
    if (!p2Kept && event.hands?.p2) p2Kept = [...event.hands.p2];
    if (!p2Kept) p2Kept = p2PeggedAll;

    // 3. Track cards played up to curIdx in this round
    const p1PlayedSoFar = [];
    const p2PlayedSoFar = [];
    for (let i = roundStartIdx; i <= curIdx; i++) {
      const e = events[i];
      if (e.type === 'peg_play' && e.card) {
        if (e.who === 'p1' || e.who === 'you') p1PlayedSoFar.push(e.card);
        else if (e.who === 'p2' || e.who === 'cpu') p2PlayedSoFar.push(e.card);
      }
    }

    let p1Hand = [];
    let p2Hand = [];

    if (curIdx === roundStartIdx && event.type === 'round_start') {
      // When a new round begins, cards have not been dealt yet
      p1Hand = [];
      p2Hand = [];
    } else if (event.type === 'deal' && p1Dealt && p2Dealt) {
      p1Hand = p1Dealt;
      p2Hand = p2Dealt;
    } else if (isP1Scoring) {
      p1Hand = (event.cards && event.cards.length) ? [...event.cards] : (p1Kept || []);
      p2Hand = ReplayModal.removeCards(p2Kept || [], p2PlayedSoFar);
    } else if (isP2Scoring) {
      p1Hand = ReplayModal.removeCards(p1Kept || [], p1PlayedSoFar);
      p2Hand = (event.cards && event.cards.length) ? [...event.cards] : (p2Kept || []);
    } else if (isShowCrib) {
      p1Hand = p1Kept || [];
      p2Hand = p2Kept || [];
    } else {
      p1Hand = ReplayModal.removeCards(p1Kept || [], p1PlayedSoFar);
      p2Hand = ReplayModal.removeCards(p2Kept || [], p2PlayedSoFar);
    }

    // 4. Crib cards
    let cribCards = [];
    if (isCribScoring && event.cards && event.cards.length) {
      cribCards = [...event.cards];
    } else if (isCribScoring && event.crib && event.crib.length) {
      cribCards = [...event.crib];
    } else if (isCribScoring && p1Discards.length && p2Discards.length) {
      cribCards = [...p1Discards, ...p2Discards];
    }

    // 5. Pegging Sequence & Count
    let pileCards = [];
    let currentCount = 0;

    if (event.type === 'peg_go' || event.type === 'peg_reset' || isShowHand || isShowCrib || event.type === 'round_start' || event.type === 'deal' || event.type === 'discard' || event.type === 'starter_cut') {
      pileCards = [];
      currentCount = 0;
    } else {
      for (let i = roundStartIdx; i <= curIdx; i++) {
        const e = events[i];
        if (e.type === 'peg_go' || e.type === 'peg_reset' || e.type === 'round_start') {
          pileCards = [];
          currentCount = 0;
        } else if (e.type === 'peg_play') {
          if (currentCount >= 31) {
            pileCards = [];
            currentCount = 0;
          }
          if (e.card) {
            pileCards.push(e.card);
          }
          currentCount = e.countAfter !== undefined ? e.countAfter : (e.count !== undefined ? e.count : currentCount);
        }
      }
    }

    // Starter card: visible only after cut has occurred in this round
    let starterCard = null;
    if (hasCutOccurred) {
      starterCard = event.starter || null;
      if (!starterCard) {
        for (let i = roundStartIdx; i <= roundEndIdx; i++) {
          if (events[i].starter) {
            starterCard = events[i].starter;
            break;
          }
        }
      }
    }

    const isCurrentCard = (c) => Boolean(event.card && c && ReplayModal.sameCard(c, event.card));

    // Calculate / resolve points & combo breakdown for show events
    let p1Pts = 0;
    let p1CombosList = [];
    if (isP1Scoring) {
      p1Pts = (event.points !== undefined && event.points !== null) ? event.points : 0;
      p1CombosList = Array.isArray(event.combos) ? [...event.combos] : [];
      const scoringCards = (p1Hand && p1Hand.length >= 4) ? p1Hand : (event.cards && event.cards.length ? event.cards : p1Kept);
      if ((!p1Pts || !p1CombosList.length) && scoringCards && scoringCards.length >= 4) {
        try {
          const sc = CribbageScorer.scoreHand(scoringCards, starterCard, false, rep.options || {});
          if (!p1Pts) p1Pts = sc.total;
          if (!p1CombosList.length) p1CombosList = sc.notes && sc.notes.length ? sc.notes : sc.lines;
        } catch (err) {
          console.warn('ReplayModal: error scoring P1 hand', err);
        }
      }
    }

    let p2Pts = 0;
    let p2CombosList = [];
    if (isP2Scoring) {
      p2Pts = (event.points !== undefined && event.points !== null) ? event.points : 0;
      p2CombosList = Array.isArray(event.combos) ? [...event.combos] : [];
      const scoringCards = (p2Hand && p2Hand.length >= 4) ? p2Hand : (event.cards && event.cards.length ? event.cards : p2Kept);
      if ((!p2Pts || !p2CombosList.length) && scoringCards && scoringCards.length >= 4) {
        try {
          const sc = CribbageScorer.scoreHand(scoringCards, starterCard, false, rep.options || {});
          if (!p2Pts) p2Pts = sc.total;
          if (!p2CombosList.length) p2CombosList = sc.notes && sc.notes.length ? sc.notes : sc.lines;
        } catch (err) {
          console.warn('ReplayModal: error scoring P2 hand', err);
        }
      }
    }

    let cribPts = 0;
    let cribCombosList = [];
    if (isCribScoring) {
      cribPts = (event.points !== undefined && event.points !== null) ? event.points : 0;
      cribCombosList = Array.isArray(event.combos) ? [...event.combos] : [];
      const scoringCards = (cribCards && cribCards.length >= 4) ? cribCards : (event.cards || event.crib || []);
      if ((!cribPts || !cribCombosList.length) && scoringCards && scoringCards.length >= 4) {
        try {
          const sc = CribbageScorer.scoreHand(scoringCards, starterCard, true, rep.options || {});
          if (!cribPts) cribPts = sc.total;
          if (!cribCombosList.length) cribCombosList = sc.notes && sc.notes.length ? sc.notes : sc.lines;
        } catch (err) {
          console.warn('ReplayModal: error scoring crib', err);
        }
      }
    }

    const activeShowPts = isCribScoring ? cribPts : (isP1Scoring ? p1Pts : (isP2Scoring ? p2Pts : 0));
    const activeShowCombos = isCribScoring ? cribCombosList : (isP1Scoring ? p1CombosList : (isP2Scoring ? p2CombosList : []));

    // Calculate running scores up to curIdx
    let runningP1 = 0;
    let runningP2 = 0;
    for (let i = roundStartIdx; i >= 0; i--) {
      const sc = events[i]?.scores;
      if (sc && (sc.p1 !== undefined || sc.you !== undefined)) {
        runningP1 = sc.p1 !== undefined ? sc.p1 : sc.you;
        runningP2 = sc.p2 !== undefined ? sc.p2 : sc.cpu;
        break;
      }
    }

    const roundDealer = events[roundStartIdx]?.dealer;
    for (let i = roundStartIdx; i <= curIdx; i++) {
      const e = events[i];
      if (e.type === 'peg_play' || e.type === 'peg_go') {
        if (e.scores) {
          const ep1 = e.scores.p1 !== undefined ? e.scores.p1 : e.scores.you;
          const ep2 = e.scores.p2 !== undefined ? e.scores.p2 : e.scores.cpu;
          if (ep1 !== undefined && ep1 !== null) runningP1 = ep1;
          if (ep2 !== undefined && ep2 !== null) runningP2 = ep2;
        }
      } else if (e.type === 'show_hand') {
        const isP1 = e.who === 'p1' || e.who === 'you';
        const isP2 = e.who === 'p2' || e.who === 'cpu';
        const pts = (i === curIdx) ? (isP1 ? p1Pts : p2Pts) : (e.points || 0);
        if (isP1) {
          const scP1 = e.scores ? (e.scores.p1 !== undefined ? e.scores.p1 : e.scores.you) : null;
          if (scP1 !== null && scP1 > runningP1) {
            runningP1 = scP1;
          } else {
            runningP1 += pts;
          }
        } else if (isP2) {
          const scP2 = e.scores ? (e.scores.p2 !== undefined ? e.scores.p2 : e.scores.cpu) : null;
          if (scP2 !== null && scP2 > runningP2) {
            runningP2 = scP2;
          } else {
            runningP2 += pts;
          }
        }
      } else if (e.type === 'show_crib') {
        const isP1 = e.who === 'p1' || e.who === 'you' || roundDealer === 'p1' || roundDealer === 'you';
        const pts = (i === curIdx) ? cribPts : (e.points || 0);
        if (isP1) {
          const scP1 = e.scores ? (e.scores.p1 !== undefined ? e.scores.p1 : e.scores.you) : null;
          if (scP1 !== null && scP1 > runningP1) {
            runningP1 = scP1;
          } else {
            runningP1 += pts;
          }
        } else {
          const scP2 = e.scores ? (e.scores.p2 !== undefined ? e.scores.p2 : e.scores.cpu) : null;
          if (scP2 !== null && scP2 > runningP2) {
            runningP2 = scP2;
          } else {
            runningP2 += pts;
          }
        }
      } else if (i === curIdx && e.scores) {
        const ep1 = e.scores.p1 !== undefined ? e.scores.p1 : e.scores.you;
        const ep2 = e.scores.p2 !== undefined ? e.scores.p2 : e.scores.cpu;
        if (ep1 !== undefined && ep1 !== null) runningP1 = ep1;
        if (ep2 !== undefined && ep2 !== null) runningP2 = ep2;
      }
    }

    const s1 = runningP1;
    const s2 = runningP2;

    const pct1 = Math.min(100, Math.max(0, (s1 / targetScore) * 100));
    const pct2 = Math.min(100, Math.max(0, (s2 / targetScore) * 100));

    // 1. Scores & Progress
    const p1ScoreEl = document.getElementById('repP1Score');
    if (p1ScoreEl) p1ScoreEl.innerHTML = `${s1} <span class="score-denom">/ ${targetScore}</span>`;
    const p2ScoreEl = document.getElementById('repP2Score');
    if (p2ScoreEl) p2ScoreEl.innerHTML = `${s2} <span class="score-denom">/ ${targetScore}</span>`;

    const p1FillEl = document.getElementById('repP1ScoreFill');
    if (p1FillEl) p1FillEl.style.width = `${pct1}%`;
    const p2FillEl = document.getElementById('repP2ScoreFill');
    if (p2FillEl) p2FillEl.style.width = `${pct2}%`;

    const roundNumEl = document.getElementById('repRoundNum');
    if (roundNumEl) roundNumEl.textContent = String(roundNum);

    const cardP1 = document.getElementById('repP1ScoreCard');
    if (cardP1) cardP1.classList.toggle('is-winner', (rep.winner === 'p1' || rep.winner === 'you') && ReplayModal.currentStep === totalSteps - 1);
    const cardP2 = document.getElementById('repP2ScoreCard');
    if (cardP2) cardP2.classList.toggle('is-winner', (rep.winner === 'p2' || rep.winner === 'cpu') && ReplayModal.currentStep === totalSteps - 1);

    // 2. Action Narration & Pause Notice
    const narrEl = document.getElementById('repNarrationText');
    if (narrEl) {
      let narrationText = event.narration || 'Play progress...';
      if (isShowHand || isShowCrib) {
        const whoLabel = isCribScoring ? 'Dealer Crib' : (isP1Scoring ? p1.name : p2.name);
        const comboText = activeShowCombos.length ? ` (${activeShowCombos.map(c => typeof c === 'string' ? c : (c.why || c.label || '')).filter(Boolean).join(', ')})` : '';
        narrationText = `⭐ ${whoLabel} scores ${activeShowPts} points in ${isCribScoring ? 'crib' : 'hand'}${comboText}`;
      }
      narrEl.textContent = narrationText;
    }

    const narrIcon = document.getElementById('repNarrationIcon');
    if (narrIcon) {
      narrIcon.textContent = isShowCrib ? '🎁' : (isShowHand ? '⭐' : (event.type === 'peg_play' ? '🃏' : '💬'));
    }

    const pauseNotice = document.getElementById('repPauseNotice');
    if (pauseNotice) {
      if (!ReplayModal.isPlaying && (isShowHand || isShowCrib)) {
        const kind = isShowCrib ? 'Crib' : (isP1Scoring ? `${p1.name}'s Hand` : `${p2.name}'s Hand`);
        pauseNotice.textContent = `⏸ Paused on ${kind} Show (+${activeShowPts} pts) — Press ▶ Play or Spacebar to continue`;
        pauseNotice.style.display = 'inline-flex';
      } else {
        pauseNotice.style.display = 'none';
      }
    }

    // 3. Starter Card & Crib
    const starterArea = document.getElementById('repStarterArea');
    if (starterArea) {
      starterArea.innerHTML = starterCard ? ReplayModal.formatCardHtml(starterCard) : '<div class="replay-card replay-card-back">🂠</div>';
    }

    const cribBox = document.getElementById('repCribBox');
    if (cribBox) cribBox.classList.toggle('is-scoring', isCribScoring);

    const cribTitle = document.getElementById('repCribTitle');
    if (cribTitle) {
      cribTitle.innerHTML = `🎁 Dealer Crib${isCribScoring ? ` <span class="replay-scoring-badge">+${cribPts} pts</span>` : ''}`;
    }

    const cribArea = document.getElementById('repCribArea');
    if (cribArea) {
      cribArea.innerHTML = cribCards && cribCards.length ? cribCards.map(c => ReplayModal.formatCardHtml(c)).join('') : '<span class="replay-dim-text">Hidden in crib</span>';
    }

    const cribCombosEl = document.getElementById('repCribCombos');
    if (cribCombosEl) {
      if (isCribScoring) {
        cribCombosEl.innerHTML = ReplayModal.formatComboPills(cribCombosList, cribPts);
        cribCombosEl.style.display = 'flex';
      } else {
        cribCombosEl.style.display = 'none';
        cribCombosEl.innerHTML = '';
      }
    }

    // 4. Pegging Sequence & Count
    const countBadge = document.getElementById('repCountBadge');
    if (countBadge) {
      countBadge.innerHTML = `Count: <strong>${currentCount}</strong> / 31`;
    }

    const pileArea = document.getElementById('repPileArea');
    if (pileArea) {
      pileArea.innerHTML = pileCards && pileCards.length ? pileCards.map((c, idx) => ReplayModal.formatCardHtml(c, idx === pileCards.length - 1)).join('') : '<span class="replay-dim-text">No cards played yet this turn</span>';
    }

    // 5. Hands & Scoring Highlights
    const p1HandBox = document.getElementById('repP1HandBox');
    if (p1HandBox) p1HandBox.classList.toggle('is-scoring', isP1Scoring);

    const p1Label = document.getElementById('repP1HandLabel');
    if (p1Label) {
      p1Label.innerHTML = `${p1.name}'s Hand:${isP1Scoring ? ` <span class="replay-scoring-badge">+${p1Pts} pts</span>` : ''}`;
    }

    const p1HandArea = document.getElementById('repP1HandArea');
    if (p1HandArea) {
      p1HandArea.innerHTML = p1Hand && p1Hand.length ? p1Hand.map(c => ReplayModal.formatCardHtml(c, isCurrentCard(c))).join('') : '<span class="replay-dim-text">No cards</span>';
    }

    const p1CombosEl = document.getElementById('repP1Combos');
    if (p1CombosEl) {
      if (isP1Scoring) {
        p1CombosEl.innerHTML = ReplayModal.formatComboPills(p1CombosList, p1Pts);
        p1CombosEl.style.display = 'flex';
      } else {
        p1CombosEl.style.display = 'none';
        p1CombosEl.innerHTML = '';
      }
    }

    const p2HandBox = document.getElementById('repP2HandBox');
    if (p2HandBox) p2HandBox.classList.toggle('is-scoring', isP2Scoring);

    const p2Label = document.getElementById('repP2HandLabel');
    if (p2Label) {
      p2Label.innerHTML = `${p2.name}'s Hand:${isP2Scoring ? ` <span class="replay-scoring-badge">+${p2Pts} pts</span>` : ''}`;
    }

    const p2HandArea = document.getElementById('repP2HandArea');
    if (p2HandArea) {
      p2HandArea.innerHTML = p2Hand && p2Hand.length ? p2Hand.map(c => ReplayModal.formatCardHtml(c, isCurrentCard(c))).join('') : '<span class="replay-dim-text">No cards</span>';
    }

    const p2CombosEl = document.getElementById('repP2Combos');
    if (p2CombosEl) {
      if (isP2Scoring) {
        p2CombosEl.innerHTML = ReplayModal.formatComboPills(p2CombosList, p2Pts);
        p2CombosEl.style.display = 'flex';
      } else {
        p2CombosEl.style.display = 'none';
        p2CombosEl.innerHTML = '';
      }
    }

    // 6. Scrubber & Step Info
    const stepInfo = document.getElementById('repStepInfo');
    if (stepInfo) {
      stepInfo.innerHTML = `Step <strong>${ReplayModal.currentStep + 1}</strong> of ${totalSteps} (Round ${roundNum})`;
    }

    const scrubber = document.getElementById('replayScrubber');
    if (scrubber) {
      scrubber.max = String(totalSteps - 1);
      if (Number(scrubber.value) !== ReplayModal.currentStep) {
        scrubber.value = String(ReplayModal.currentStep);
      }
    }

    // 7. Play / Pause Button state
    const playBtn = document.getElementById('btnRepPlayPause');
    if (playBtn) {
      playBtn.textContent = ReplayModal.isPlaying ? '⏸ Pause' : '▶ Play';
    }

    // 8. Speed Buttons & Checkbox Sync
    document.querySelectorAll('.replay-speed-btn').forEach(btn => {
      const spd = Number(btn.getAttribute('data-speed'));
      btn.classList.toggle('is-active', ReplayModal.playbackSpeed === spd);
    });

    const chkPause = document.getElementById('chkAutoPauseShow');
    if (chkPause && chkPause.checked !== ReplayModal.autoPauseOnShow) {
      chkPause.checked = ReplayModal.autoPauseOnShow;
    }
  }

  /**
   * Binds interaction events for the interactive player view.
   */
  static bindPlayerEvents() {
    const el = document.getElementById(ReplayModal.MODAL_ID);
    if (!el) return;

    el.querySelector('#btnReplayClose')?.addEventListener('click', () => ReplayModal.close());
    el.querySelector('#btnReplayBackToLib')?.addEventListener('click', () => ReplayModal.openLibrary());

    el.querySelector('#btnRepPlayPause')?.addEventListener('click', () => ReplayModal.togglePlayPause());
    el.querySelector('#btnRepStepForward')?.addEventListener('click', () => ReplayModal.stepForward());
    el.querySelector('#btnRepStepBack')?.addEventListener('click', () => ReplayModal.stepBackward());
    el.querySelector('#btnRepPrevRound')?.addEventListener('click', () => ReplayModal.jumpRound(-1));
    el.querySelector('#btnRepNextRound')?.addEventListener('click', () => ReplayModal.jumpRound(1));

    const chkPause = el.querySelector('#chkAutoPauseShow');
    if (chkPause) {
      chkPause.addEventListener('change', (e) => {
        ReplayModal.autoPauseOnShow = Boolean(e.target.checked);
      });
    }

    const scrubber = el.querySelector('#replayScrubber');
    if (scrubber) {
      scrubber.addEventListener('input', (e) => {
        ReplayModal.goToStep(Number(e.target.value));
      });
    }

    el.querySelectorAll('.replay-speed-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const spd = Number(btn.getAttribute('data-speed')) || 1;
        ReplayModal.setPlaybackSpeed(spd);
      });
    });
  }

  /**
   * Jumps timeline to specific step.
   * @param {number} step
   */
  static goToStep(step) {
    if (!ReplayModal.activeReplay) return;
    const events = ReplayModal.activeReplay.events || [];
    const target = Math.max(0, Math.min(events.length - 1, step));
    ReplayModal.currentStep = target;
    ReplayModal.renderPlayer();
  }

  /**
   * Steps forward 1 event in the timeline.
   * @param {boolean} [isAutoPlay=false] - Whether triggered by the auto-playback timer
   */
  static stepForward(isAutoPlay = false) {
    if (!ReplayModal.activeReplay) return;
    const events = ReplayModal.activeReplay.events || [];
    if (ReplayModal.currentStep < events.length - 1) {
      const nextStep = ReplayModal.currentStep + 1;
      ReplayModal.goToStep(nextStep);

      // Auto-pause after showing hand or crib points during playback
      if (isAutoPlay && ReplayModal.autoPauseOnShow) {
        const nextEv = events[nextStep];
        if (nextEv && (nextEv.type === 'show_hand' || nextEv.type === 'show_crib')) {
          ReplayModal.stopPlayback();
          ReplayModal.renderPlayer();
        }
      }
    } else {
      ReplayModal.stopPlayback();
    }
  }

  /**
   * Steps backward 1 event in the timeline.
   */
  static stepBackward() {
    if (!ReplayModal.activeReplay) return;
    if (ReplayModal.currentStep > 0) {
      ReplayModal.goToStep(ReplayModal.currentStep - 1);
    }
  }

  /**
   * Jumps to the beginning of the next or previous round.
   * @param {number} delta - -1 for prev round, 1 for next round
   */
  static jumpRound(delta) {
    if (!ReplayModal.activeReplay) return;
    const events = ReplayModal.activeReplay.events || [];
    const curEvent = events[ReplayModal.currentStep] || {};
    const curRound = curEvent.round || 1;

    if (delta > 0) {
      const idx = events.findIndex(e => e.round > curRound);
      if (idx !== -1) ReplayModal.goToStep(idx);
      else ReplayModal.goToStep(events.length - 1);
    } else {
      let idx = -1;
      for (let i = ReplayModal.currentStep - 1; i >= 0; i--) {
        if (events[i].round < curRound) {
          // Find first event of that previous round
          const targetRound = events[i].round;
          idx = events.findIndex(e => e.round === targetRound);
          break;
        }
      }
      ReplayModal.goToStep(idx !== -1 ? idx : 0);
    }
  }

  /**
   * Toggles playback play / pause state.
   */
  static togglePlayPause() {
    if (ReplayModal.isPlaying) {
      ReplayModal.stopPlayback();
    } else {
      ReplayModal.startPlayback();
    }
  }

  /**
   * Starts automatic playback.
   */
  static startPlayback() {
    if (!ReplayModal.activeReplay) return;
    ReplayModal.stopPlayback();
    ReplayModal.isPlaying = true;

    const events = ReplayModal.activeReplay.events || [];
    if (ReplayModal.currentStep >= events.length - 1) {
      ReplayModal.currentStep = 0;
      ReplayModal.goToStep(0);
    }

    const notice = document.getElementById('repPauseNotice');
    if (notice) notice.style.display = 'none';

    const interval = Math.max(250, Math.round(1200 / ReplayModal.playbackSpeed));
    ReplayModal.playbackTimer = setInterval(() => {
      if (ReplayModal.currentStep < events.length - 1) {
        ReplayModal.stepForward(true);
      } else {
        ReplayModal.stopPlayback();
      }
    }, interval);

    const btn = document.getElementById('btnRepPlayPause');
    if (btn) btn.textContent = '⏸ Pause';
  }

  /**
   * Stops automatic playback.
   */
  static stopPlayback() {
    ReplayModal.isPlaying = false;
    if (ReplayModal.playbackTimer) {
      clearInterval(ReplayModal.playbackTimer);
      ReplayModal.playbackTimer = null;
    }
    const btn = typeof document !== 'undefined' ? document.getElementById('btnRepPlayPause') : null;
    if (btn) btn.textContent = '▶ Play';
  }

  /**
   * Adjusts playback speed.
   * @param {number} speed - 0.5, 1, 2, or 4
   */
  static setPlaybackSpeed(speed) {
    ReplayModal.playbackSpeed = speed;
    if (ReplayModal.isPlaying) {
      ReplayModal.startPlayback();
    }
    document.querySelectorAll('.replay-speed-btn').forEach(btn => {
      const spd = Number(btn.getAttribute('data-speed'));
      btn.classList.toggle('is-active', ReplayModal.playbackSpeed === spd);
    });
  }

  /**
   * Renders the saved replays library view separated by brand.
   * @param {string} [brandFilter=null]
   */
  static renderLibrary(brandFilter = null) {
    const overlay = document.getElementById(ReplayModal.OVERLAY_ID);
    if (!overlay) return;

    ReplayModal._currentView = 'library';
    ReplayModal._renderedReplayId = null;

    if (brandFilter !== null && brandFilter !== undefined) {
      ReplayModal._selectedBrand = brandFilter;
    }
    const currentBrand = ReplayModal._selectedBrand || 'all';

    const replays = GameReplayManager.loadReplays();
    const autoRecord = GameReplayManager.isAutoRecordEnabled();

    const brands = (typeof GAME_BRANDS !== 'undefined' && Array.isArray(GAME_BRANDS)) ? GAME_BRANDS : [
      { id: 'crazy', name: 'Crazy Cribbage', shortName: 'Crazy', icon: '🃏', color: '#facc15', badgeBg: 'rgba(245, 158, 11, 0.2)', border: 'rgba(245, 158, 11, 0.4)' },
      { id: 'clash', name: 'Cribbage Clash', shortName: 'Clash', icon: '⚔️', color: '#38bdf8', badgeBg: 'rgba(56, 189, 248, 0.2)', border: 'rgba(56, 189, 248, 0.4)' },
      { id: 'pints', name: 'Pints & Pegs', shortName: 'Pints', icon: '🍺', color: '#10b981', badgeBg: 'rgba(16, 185, 129, 0.2)', border: 'rgba(16, 185, 129, 0.4)' },
      { id: 'adult', name: 'Skunked & Stripped', shortName: 'Adult', icon: '🦨', color: '#ec4899', badgeBg: 'rgba(244, 63, 94, 0.2)', border: 'rgba(244, 63, 94, 0.4)' }
    ];

    const counts = {
      all: replays.length,
      crazy: replays.filter(r => (r.brand || 'crazy') === 'crazy').length,
      clash: replays.filter(r => (r.brand || 'crazy') === 'clash').length,
      pints: replays.filter(r => (r.brand || 'crazy') === 'pints').length,
      adult: replays.filter(r => (r.brand || 'crazy') === 'adult').length
    };

    const renderCard = (r) => {
      const bId = r.brand || 'crazy';
      const bObj = brands.find(b => b.id === bId) || brands[0];
      const p1 = r.players?.p1 || { name: 'You', avatar: '🤠' };
      const p2 = r.players?.p2 || { name: 'Computer', avatar: '🤖' };
      const s1 = r.finalScores?.p1 || 0;
      const s2 = r.finalScores?.p2 || 0;
      const isWinnerP1 = r.winner === 'p1' || r.winner === 'you';
      return `
        <div class="replay-card-item rep-card-${bId}" data-id="${r.id}" style="border: 1.5px solid ${bObj.border}; background: rgba(18, 14, 28, 0.65); box-shadow: 0 4px 15px rgba(0,0,0,0.35);">
          <div class="rep-card-top" style="display:flex; justify-content:space-between; align-items:center;">
            <span class="rep-badge-brand" style="color:${bObj.color}; background:${bObj.badgeBg}; border:1px solid ${bObj.border}; padding:2px 8px; border-radius:12px; font-weight:800; font-size:0.73rem;">
              ${bObj.icon} ${r.brandLabel || bObj.name}
            </span>
            <span class="rep-badge-date" style="font-size:0.72rem; color:#94a3b8;">${r.dateLabel || 'Match'}</span>
          </div>

          <div class="rep-card-matchup">
            <div class="rep-player-col ${isWinnerP1 ? 'is-winner' : ''}">
              <span class="rep-player-avatar">${p1.avatar || '🤠'}</span>
              <span class="rep-player-name">${p1.name}</span>
              <span class="rep-player-score">${s1}</span>
            </div>
            <div class="rep-vs-divider">VS</div>
            <div class="rep-player-col ${!isWinnerP1 ? 'is-winner' : ''}">
              <span class="rep-player-avatar">${p2.avatar || '🤖'}</span>
              <span class="rep-player-name">${p2.name}</span>
              <span class="rep-player-score">${s2}</span>
            </div>
          </div>

          <div class="rep-card-bottom">
            <div class="rep-winner-tag">
              🏆 Winner: <strong>${r.winnerName || 'Winner'}</strong> ${r.skunkKind ? `<span class="skunk-tag">${r.skunkKind.toUpperCase()} SKUNK</span>` : ''}
            </div>
            <div class="rep-actions-row">
              <button type="button" class="btn-gold btn-play-rep" data-id="${r.id}">▶ Watch Replay</button>
              <button type="button" class="btn-ghost btn-del-rep" data-id="${r.id}" title="Delete Replay">🗑</button>
            </div>
          </div>
        </div>
      `;
    };

    overlay.innerHTML = `
      <div class="replay-modal-wrap replay-library-wrap" id="${ReplayModal.MODAL_ID}">
        <!-- Header -->
        <div class="replay-header">
          <div class="replay-header-left">
            <h2 class="replay-title">🎬 Game Replays Library</h2>
            <p class="replay-subtitle">Watch and review your past matches move-by-move across all 4 games.</p>
          </div>
          <div class="replay-header-right">
            <button type="button" class="btn-ghost replay-btn-close" id="btnLibClose" title="Close">✕</button>
          </div>
        </div>

        <!-- Brand & Game Filter Dropdown (replaces overflowing horizontal scrollbar) -->
        <div class="replay-brand-nav-tabs replay-brand-nav-dropdown-row" id="replayBrandFilterRow" style="display:flex; align-items:center; justify-content:space-between; gap:10px; padding:10px 20px; background:rgba(0,0,0,0.38); border-bottom:1px solid rgba(240,198,74,0.2); flex-wrap:wrap; overflow:visible;">
          <div style="display:flex; align-items:center; gap:8px;">
            <span style="font-size:1.15rem;">🎯</span>
            <label for="selectReplayBrandFilter" style="font-size:0.84rem; font-weight:800; color:#f0c64a; text-transform:uppercase; letter-spacing:0.04em;">
              Filter Replays By Game:
            </label>
          </div>
          <div style="flex:1; min-width:240px; position:relative;">
            <select id="selectReplayBrandFilter" class="replay-brand-select" aria-label="Filter replays by game or brand" style="width:100%; padding:8px 32px 8px 12px; border-radius:8px; border:1.5px solid #f0c64a; background:#0f172a; color:#f0c64a; font-weight:700; font-size:0.9rem; cursor:pointer; outline:none; appearance:none; -webkit-appearance:none; box-shadow:0 2px 8px rgba(0,0,0,0.4);">
              <option value="all" ${currentBrand === 'all' ? 'selected' : ''}>🌐 All Brands (${counts.all} ${counts.all === 1 ? 'Replay' : 'Replays'})</option>
              ${brands.map(b => `
                <option value="${b.id}" ${currentBrand === b.id ? 'selected' : ''}>${b.icon} ${b.name} (${counts[b.id]} ${counts[b.id] === 1 ? 'Replay' : 'Replays'})</option>
              `).join('')}
            </select>
            <span style="position:absolute; right:12px; top:50%; transform:translateY(-50%); pointer-events:none; color:#f0c64a; font-size:0.75rem;">▼</span>
          </div>
        </div>

        <!-- Toolbar & Quick Actions -->
        <div class="replay-lib-toolbar">
          <div class="replay-lib-toolbar-left">
            <label class="replay-toggle-label" title="Automatically record every completed game">
              <input type="checkbox" id="chkAutoRecord" ${autoRecord ? 'checked' : ''} />
              <span>Auto-Record Matches</span>
            </label>
          </div>
        </div>

        <!-- Replay Match Cards List -->
        <div class="replay-list-container">
          ${replays.length === 0 ? `
            <div class="replay-empty-state">
              <div class="empty-icon">🎬</div>
              <h3>No Recorded Games Yet</h3>
              <p>Play a match against the computer or online with friends across any of the 4 games, and your full game replay will appear here!</p>
            </div>
          ` : currentBrand === 'all' ? `
            <!-- All Brands View Separated by Brand -->
            ${brands.map(b => {
              const brandReplays = replays.filter(r => (r.brand || 'crazy') === b.id);
              return `
                <div class="rep-brand-section" data-brand="${b.id}" style="margin-bottom: 24px;">
                  <div class="rep-brand-header" style="display:flex; justify-content:space-between; align-items:center; padding:9px 14px; border-radius:8px; border:1px solid ${b.border}; border-left:4px solid ${b.color}; background: linear-gradient(90deg, ${b.badgeBg}, rgba(0,0,0,0.3)); margin-bottom:12px;">
                    <div style="display:flex; align-items:center; gap:8px;">
                      <span style="font-size:1.3rem;">${b.icon}</span>
                      <span style="color:${b.color}; font-weight:800; font-size:1.02rem;">${b.name}</span>
                    </div>
                    <span style="background:${b.badgeBg}; border:1px solid ${b.border}; color:${b.color}; padding:2px 8px; border-radius:12px; font-size:0.75rem; font-weight:800;">
                      ${brandReplays.length} ${brandReplays.length === 1 ? 'Replay' : 'Replays'}
                    </span>
                  </div>
                  ${brandReplays.length === 0 ? `
                    <div class="rep-brand-empty-hint" style="font-size:0.82rem; color:#94a3b8; font-style:italic; padding:12px 14px; background:rgba(0,0,0,0.2); border-radius:8px; border:1px dashed rgba(244,234,212,0.1); text-align:center;">
                      No ${b.name} replays saved yet. Play a match in ${b.name} to save your gameplay!
                    </div>
                  ` : `
                    <div class="replay-grid">
                      ${brandReplays.map(renderCard).join('')}
                    </div>
                  `}
                </div>
              `;
            }).join('')}
          ` : (() => {
            const b = brands.find(item => item.id === currentBrand) || brands[0];
            const brandReplays = replays.filter(r => (r.brand || 'crazy') === b.id);
            return `
              <!-- Single Brand View -->
              <div class="rep-brand-section rep-brand-section-focused" data-brand="${b.id}">
                <div class="rep-brand-header" style="display:flex; justify-content:space-between; align-items:center; padding:9px 14px; border-radius:8px; border:1px solid ${b.border}; border-left:4px solid ${b.color}; background: linear-gradient(90deg, ${b.badgeBg}, rgba(0,0,0,0.3)); margin-bottom:12px;">
                  <div style="display:flex; align-items:center; gap:8px;">
                    <span style="font-size:1.3rem;">${b.icon}</span>
                    <span style="color:${b.color}; font-weight:800; font-size:1.02rem;">${b.name} Replays</span>
                  </div>
                  <span style="background:${b.badgeBg}; border:1px solid ${b.border}; color:${b.color}; padding:2px 8px; border-radius:12px; font-size:0.75rem; font-weight:800;">
                    ${brandReplays.length} ${brandReplays.length === 1 ? 'Replay' : 'Replays'}
                  </span>
                </div>
                ${brandReplays.length === 0 ? `
                  <div class="replay-empty-state">
                    <div class="empty-icon">${b.icon}</div>
                    <h3>No Recorded ${b.name} Games Yet</h3>
                    <p>Play a match in ${b.name}, and your full game replay will appear here!</p>
                  </div>
                ` : `
                  <div class="replay-grid">
                    ${brandReplays.map(renderCard).join('')}
                  </div>
                `}
              </div>
            `;
          })()}
        </div>
      </div>
    `;

    ReplayModal.bindLibraryEvents();
  }

  /**
   * Binds interaction events for the library view.
   */
  static bindLibraryEvents() {
    const el = document.getElementById(ReplayModal.MODAL_ID);
    if (!el) return;

    el.querySelector('#btnLibClose')?.addEventListener('click', () => ReplayModal.close());

    // Brand filter dropdown
    const brandSelect = el.querySelector('#selectReplayBrandFilter');
    if (brandSelect) {
      brandSelect.addEventListener('change', (e) => {
        ReplayModal.renderLibrary(e.target.value);
      });
    }

    // Brand filter tabs (legacy/fallback)
    el.querySelectorAll('.replay-brand-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        const bId = btn.getAttribute('data-brand') || 'all';
        ReplayModal.renderLibrary(bId);
      });
    });

    // Auto-record toggle
    const chk = el.querySelector('#chkAutoRecord');
    if (chk) {
      chk.addEventListener('change', (e) => {
        GameReplayManager.setAutoRecordEnabled(e.target.checked);
      });
    }

    // Per-card actions
    el.querySelectorAll('.btn-play-rep').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        ReplayModal.open(id);
      });
    });

    el.querySelectorAll('.btn-del-rep').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        if (confirm('Delete this recorded replay?')) {
          GameReplayManager.deleteReplay(id);
          ReplayModal.renderLibrary(ReplayModal._selectedBrand);
        }
      });
    });
  }

  /**
   * Displays the share / export dialog for a specific replay.
   * @param {Object} replay
   */
  static async openShareDialog(replay) {
    if (!replay) return;

    let shareUrl = '';
    try {
      shareUrl = await GameReplayManager.generateShareUrl(replay);
    } catch (_) {}

    const dialogId = 'crazyReplayShareDialog';
    let dialog = document.getElementById(dialogId);
    if (!dialog) {
      dialog = document.createElement('div');
      dialog.id = dialogId;
      dialog.className = 'replay-share-dialog';
      document.body.appendChild(dialog);
    }

    dialog.innerHTML = `
      <div class="replay-share-box">
        <div class="share-box-header">
          <h3>📤 Share Match Replay</h3>
          <button type="button" class="btn-ghost share-box-close" id="btnCloseShareDialog">✕</button>
        </div>
        <p class="share-box-desc">Share this game with friends so they can watch the match play out move-by-move!</p>

        <div class="share-options-list">
          <!-- 1. Download File -->
          <div class="share-option-card">
            <div class="share-card-info">
              <strong>📥 Download Replay File (.cribbage.json)</strong>
              <span>Save the match file locally to share or store.</span>
            </div>
            <button type="button" class="btn-gold replay-btn-sm" id="btnDlReplayFile">Download File</button>
          </div>

          <!-- 2. Copy Share Link -->
          ${shareUrl ? `
            <div class="share-option-card">
              <div class="share-card-info">
                <strong>🔗 Shareable Web Link</strong>
                <span>Open directly in any browser with zero setup.</span>
              </div>
              <button type="button" class="btn-gold replay-btn-sm" id="btnCopyShareUrl">Copy Link</button>
            </div>
          ` : ''}

          <!-- 3. Copy JSON to Clipboard -->
          <div class="share-option-card">
            <div class="share-card-info">
              <strong>📋 Copy Replay JSON</strong>
              <span>Copy full match events string to clipboard.</span>
            </div>
            <button type="button" class="btn-ghost replay-btn-sm" id="btnCopyReplayJson">Copy JSON</button>
          </div>
        </div>
      </div>
    `;

    dialog.style.display = 'flex';

    const replayOverlay = document.getElementById(ReplayModal.OVERLAY_ID);
    const isReplayOverlayOpen = Boolean(replayOverlay && replayOverlay.style.display !== 'none');

    const closeDialog = () => {
      dialog.style.display = 'none';
      if (typeof window !== 'undefined') {
        window.removeEventListener('keydown', onShareDialogKeydown);
      }
      if (!isReplayOverlayOpen) {
        const isGameOver = typeof window !== 'undefined' && ((window.state && window.state.over) || window._lastGameOverHtml);
        if (isGameOver) {
          if (typeof window !== 'undefined' && typeof window.showGameWonModal === 'function') {
            window.showGameWonModal();
          } else if (typeof GameEngine !== 'undefined' && typeof GameEngine.showGameWonModal === 'function') {
            GameEngine.showGameWonModal();
          }
        }
      }
    };

    const onShareDialogKeydown = (e) => {
      if (e.key === 'Escape' && dialog.style.display !== 'none') {
        e.preventDefault();
        e.stopPropagation();
        closeDialog();
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', onShareDialogKeydown);
    }

    dialog.onclick = (e) => {
      if (e.target === dialog) {
        closeDialog();
      }
    };

    dialog.querySelector('#btnCloseShareDialog')?.addEventListener('click', closeDialog);

    dialog.querySelector('#btnDlReplayFile')?.addEventListener('click', () => {
      GameReplayManager.exportReplayFile(replay);
      if (typeof GameReplayManager !== 'undefined' && GameReplayManager.showToast) {
        GameReplayManager.showToast('Replay file downloaded!', 2500);
      } else if (typeof window !== 'undefined' && window.showScoreToast) {
        window.showScoreToast('Replay file downloaded!', 2500);
      }
    });

    dialog.querySelector('#btnCopyShareUrl')?.addEventListener('click', async () => {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        if (typeof GameReplayManager !== 'undefined' && GameReplayManager.showToast) {
          GameReplayManager.showToast('Share link copied to clipboard!', 2500);
        } else if (typeof window !== 'undefined' && window.showScoreToast) {
          window.showScoreToast('Share link copied to clipboard!', 2500);
        }
      } else {
        prompt('Copy your replay share link:', shareUrl);
      }
    });

    dialog.querySelector('#btnCopyReplayJson')?.addEventListener('click', async () => {
      const json = JSON.stringify(replay, null, 2);
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(json);
        if (typeof GameReplayManager !== 'undefined' && GameReplayManager.showToast) {
          GameReplayManager.showToast('Replay JSON copied to clipboard!', 2500);
        } else if (typeof window !== 'undefined' && window.showScoreToast) {
          window.showScoreToast('Replay JSON copied to clipboard!', 2500);
        }
      } else {
        prompt('Copy your replay JSON:', json);
      }
    });
  }
}

if (typeof window !== 'undefined') {
  window.ReplayModal = ReplayModal;
}
